import { useEffect, useRef, useState, type ReactNode } from "react";

import { Icone } from "../ui/icons";
import type { NomeDeIcone } from "../ui/icones-do-jogo";
import { useDialogoDeAtalhos } from "../ui/useAtalhos";

/**
 * Um ícone do rodapé, com o número em cima, que abre uma caixa por cima.
 *
 * É o molde da fila da rodada e das propostas recebidas, ao lado do sino e do
 * botão de jogar: a mesma caixa do sino, clique fora e Esc fecham.
 */
export function BotaoComGaveta({
  icone,
  rotulo,
  conta,
  classeDaConta,
  nomeDoDialogo,
  children,
}: {
  icone: NomeDeIcone;
  /** O título da caixa, que também é o nome do botão para leitor de tela. */
  rotulo: string;
  conta: number;
  /** A cor do número: cada botão tem a sua, e só o sino é vermelho. */
  classeDaConta: string;
  nomeDoDialogo: string;
  children: ReactNode;
}): ReactNode {
  const [aberto, definirAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const fora = (evento: PointerEvent): void => {
      if (raiz.current && !raiz.current.contains(evento.target as Node)) definirAberto(false);
    };
    window.addEventListener("pointerdown", fora);
    return () => window.removeEventListener("pointerdown", fora);
  }, [aberto]);

  return (
    <div className="sino-raiz" ref={raiz}>
      <button
        type="button"
        className="ghost sino-botao"
        aria-label={rotulo}
        aria-expanded={aberto}
        onClick={() => definirAberto((atual) => !atual)}
      >
        <Icone nome={icone} size={18} />
        {conta > 0 && <span className={`sino-conta ${classeDaConta} num`}>{conta > 99 ? "99+" : conta}</span>}
      </button>

      {aberto && (
        <Gaveta nome={nomeDoDialogo} aoFechar={() => definirAberto(false)}>
          <div className="sino-topo">
            <b>{rotulo}</b>
          </div>
          <div className="sino-lista">{children}</div>
        </Gaveta>
      )}
    </div>
  );
}

function Gaveta({ nome, aoFechar, children }: { nome: string; aoFechar: () => void; children: ReactNode }): ReactNode {
  useDialogoDeAtalhos({ nome, nivel: 20, aoFechar });
  return (
    <div className="sino-gaveta" role="dialog">
      {children}
    </div>
  );
}
